import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { Color } from 'src/app/shared/types';
import { ButtonComponent } from 'src/app/ui-kit/button/button.component';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Compact action card for dashboards and landing pages. Displays an icon, title,
 * subtitle, and a trailing call-to-action button.
 *
 * The card is built on `<app-card>` so it inherits shared container styling,
 * border, shadow, and dark-mode adaptation. Fire the `(action)` output from the
 * trailing button — or supply any button label / color you need via inputs.
 *
 * @example
 * <app-quick-action-card
 *   icon="add-circle-outline"
 *   title="New Goal"
 *   subtitle="Create a new IEP goal for a student"
 *   buttonLabel="Create"
 *   (action)="openNewGoalDialog()"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-quick-action-card',
  templateUrl: './quick-action-card.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, CardComponent, IconComponent],
  host: { class: 'block' },
})
export class QuickActionCardComponent {
  /** Ionicon name rendered at the top of the card. */
  readonly icon = input<string>('');

  /** Card headline. */
  readonly title = input<string>('');

  /** Supporting description rendered below the title. */
  readonly subtitle = input<string>('');

  /**
   * Label shown inside the trailing button.
   * @default 'Go'
   */
  readonly buttonLabel = input<string>('Go');

  /**
   * Brand color applied to the trailing button.
   * @default 'purple'
   */
  readonly buttonColor = input<Color>('purple');

  /** Fires when the trailing button is clicked. */
  readonly action = output<void>();

  onActionClick(): void {
    this.action.emit();
  }
}
